// View HUD: the dynamic layer over what the player looks at (compass strip,
// projected feature labels, reticle, range, MAG). ONE painter, paintViewHud,
// on two surfaces with one layout rule (_viewLayout):
//   • desktop + phone — the fullscreen DOM canvas #view-hud (ScopeHud) over
//     the one view: compass always, the centre dot morphing into the reticle
//     with the zoom (w), the centre range always, labels by the zoom's
//     visibility layers (APP_VIEW §5).
//   • VR — the head zoom window's 1024×640 overlay (paintZoomWindow ←
//     Weapons.drawLabels): the same HUD over the magnified picture, a plain
//     rectangle (owner 2026-11-01: no binocular housing).
// The layout object L carries every position/scale.
import { t } from './i18n.js';
import './i18n/player.js';

// HUD phosphor colors (cyan primary, amber secondary, water blue)
const cyan = (a) => `rgba(0,255,200,${a})`;
const _am = (a) => `rgba(255,190,60,${a})`;
const _wa = (a) => `rgba(80,180,255,${a})`;
const _cy = cyan;
const HALO = 'rgba(0,0,0,0.78)';   // the leader's dark halo (paintLabels)

// the compass letters, resolved in the current language at each paint
// (a big letter = the four main cardinals, nd % 90 === 0)
const CARDINALS = {
  0: () => t('player.scope.n'), 45: () => t('player.scope.ne'),
  90: () => t('player.scope.e'), 135: () => t('player.scope.se'),
  180: () => t('player.scope.s'), 225: () => t('player.scope.sw'),
  270: () => t('player.scope.w'), 315: () => t('player.scope.nw'),
};
// exported for the inspect card header (inspect.js)
export const LABEL_ICON = {
  peak: '▲', farpeak: '▲', pass: '⌒', ridge: '⋀',
  church: '⛪', chapel: '⛪', cathedral: '⛪',
  museum: '🏛', gallery: '🏛', attraction: '🏛',
  hotel: '🏨', hostel: '🏨',
  restaurant: '🍽', cafe: '☕', bar: '🍺',
  alpine_hut: '⛺', viewpoint: '👁',
  lake: '🌊', river: '🌊', station: '🚉',
};

// the design space is 640 tall; a landscape phone is ~390 css px, so a
// straight scale would print 6 px labels. Geometry shrinks with L.font,
// text never goes below this (the center boost grows it again).
const TXT_MIN = 12;

// Label rows (gazetteer.inView packs into LABEL_ROWS, row 0 highest). A peak
// label is two lines — the 19 px name above the row baseline, the 11 px
// distance below it, ~33 px — so the pitch is LABEL_ROW_PX: at the old 24 px
// a label stacked into the next row (the packer stacks exactly the labels
// that overlap in x) ran its name through the distance line above it.
export const LABEL_ROWS = 5;
const LABEL_ROW_PX = 34;

// Label legibility (every device; owner 2026-10-20 "on a phone they are almost
// invisible, not enough contrast"): the text is white (amber / blue / cyan by
// kind) on a dark translucent pill under its dark outline, and is NEVER faded —
// `alpha` (the 1× summit layer, labels_1x_alpha) fades the leader, box and
// diamond only. Before, the whole layer faded to 0.45: the white name and its
// black outline both went grey on a pale sky. check-labels "paint" proves every
// text colour ≥ 4.5:1 on its pill over a white sky.
export const LABEL_PILL = [6, 10, 16, 0.72];        // rgba of the backing pill
export const LABEL_INK = {                          // text colours by kind
  name: [255, 255, 255], dist: [140, 255, 230],     // summits / passes / ridges
  station: [0, 255, 200], water: [130, 205, 255], poi: [255, 220, 100],
};
const _rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
const PILL = `rgba(${LABEL_PILL.join(',')})`;
const INK = [_rgb(LABEL_INK.name), _rgb(LABEL_INK.station), _rgb(LABEL_INK.water), _rgb(LABEL_INK.poi)];
const SUB = [_rgb(LABEL_INK.dist), _rgb(LABEL_INK.station), _rgb(LABEL_INK.water), _rgb(LABEL_INK.poi)];
// the phone's floor for a label name (CSS px; summits — the other kinds 1 less)
export const PHONE_NAME_MIN = 15;

// Place the labels out of the HUD (desktop + phone; owner 2026-10-20 "move them
// higher, but avoid placing over the minimap"). Pure, preallocated, no DOM.
// Label i: pill box [x0, x1] × [y0, y1], row baseline ly, its box bottom may
// sink to lim (it stays above its object). In `ord` order (row 0 first) each
// label drops by whole rows (`slot` px) until its box clears every avoid rect
// ([x0, y0, x1, y1] × nAvoid) and no placed label sits in the same row slot
// overlapping it in x. No such slot down to lim: dy = NaN (not drawn — a
// label on the HUD or through another one is not readable). Writes dy[i].
export function placeLabels(n, x0, x1, y0, y1, ly, lim, slot, avoid, nAvoid, ord, dy) {
  for (let a = 0; a < n; a++) {
    const i = ord[a];
    let free = NaN;
    for (let d = 0; d === 0 || y1[i] + d <= lim[i]; d += slot) {
      let hit = false;
      for (let r = 0; r < nAvoid && !hit; r++) {
        const o = 4 * r;
        hit = x0[i] < avoid[o + 2] && avoid[o] < x1[i] && y0[i] + d < avoid[o + 3] && avoid[o + 1] < y1[i] + d;
      }
      if (hit) continue;
      for (let b = 0; b < a && !hit; b++) {
        const j = ord[b];
        hit = dy[j] === dy[j] && x0[i] < x1[j] && x0[j] < x1[i]
          && Math.abs(ly[i] + d - ly[j] - dy[j]) < slot - 0.5;
      }
      if (!hit) { free = d; break; }
    }
    dy[i] = free;
  }
}

// per-paint scratch (no allocation per frame): the boxes, rows and text of
// the labels on screen
const MAXL = 64;
const _X0 = new Float32Array(MAXL), _X1 = new Float32Array(MAXL);
const _Y0 = new Float32Array(MAXL), _Y1 = new Float32Array(MAXL);
const _LY = new Float32Array(MAXL), _LIM = new Float32Array(MAXL), _DY = new Float32Array(MAXL);
const _SXa = new Float32Array(MAXL), _SYa = new Float32Array(MAXL), _TW = new Float32Array(MAXL);
const _NP = new Uint8Array(MAXL), _DP = new Uint8Array(MAXL), _KC = new Uint8Array(MAXL);
const _ORD = new Int16Array(MAXL);
const _FE = new Array(MAXL).fill(null), _EN = new Array(MAXL).fill(null);
// font strings per (style, px): 0 bold sans, 1 sans, 2 mono
const _FONTS = [new Map(), new Map(), new Map()];
function _font(st, px) {
  let s = _FONTS[st].get(px);
  if (!s) {
    s = st === 0 ? `bold ${px}px system-ui, sans-serif` : st === 1 ? `${px}px system-ui, sans-serif`
      : `${px}px ui-monospace, monospace`;
    _FONTS[st].set(px, s);
  }
  return s;
}
// label text by name, rebuilt only when its kind / ele / shown distance
// changes; widths measured once at 100 px (w, dw = width per px)
const _TXT = new Map();
function _entry(ctx, f, kc) {
  let e = _TXT.get(f.name);
  if (!e || e.kind !== f.kind || e.ele !== f.ele) {
    if (_TXT.size > 2000) _TXT.clear();
    const icon = LABEL_ICON[f.kind] || '📍';
    const s = kc === 0 ? `${icon} ${f.name}` + (f.ele ? ` ${f.ele} m` : '') : `${icon} ${f.name}`;
    ctx.font = _font(kc <= 1 ? 0 : 1, 100);
    e = { kind: f.kind, ele: f.ele, s, w: ctx.measureText(s).width / 100, dk: NaN, d: '', dw: 0 };
    _TXT.set(f.name, e);
  }
  const m = f.dist;
  const dk = kc === 0 && m >= 10000 ? 1e6 + Math.round(m / 1000) : m >= 1000 ? 1000 + Math.round(m / 100) : m;
  if (dk !== e.dk) {
    e.dk = dk;
    e.d = kc === 0 && m >= 10000 ? `${Math.round(m / 1000)} km` : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
    ctx.font = _font(2, 100);
    e.dw = ctx.measureText(e.d).width / 100;
  }
  return e;
}

// Projected feature labels: name (+ elevation) and distance on a pill, a
// leader line down to a designator box on the feature. Shared by both
// painters. The leader + box are TWO-TONE — a dark halo under a bright core —
// so they read on bright sky and snow as on dark forest, in VR the same (the
// one painter; an inverting blend cannot reach the 3D image from a DOM canvas
// or the binocular's canvas texture). f.occl (gazetteer markOccluded: the
// object sits behind something nearer on the optic's depth): DASHED. alpha < 1
// fades the leader, box and diamond (the 1× summit layer), never the text;
// f.off (set by main.js reprojection) skips a feature that left the view.
// boost: the canvas pixels land much smaller on a phone — grow the font of
// whatever is being glassed, peaking ~2x at the reticle, base at the rim;
// L.nameMin floors the name (the phone's PHONE_NAME_MIN).
// Row pitch: LABEL_ROW_PX scaled, never less than the two floored text
// lines of a peak label (+4 px) — a phone's 12 px floor outgrows the scale.
// L.avoid / L.nAvoid: the HUD rects — placeLabels drops a label out of them;
// without, every label sits on its row.
// Drawn in passes — leaders, then every pill, then every text — so a pill
// never covers a neighbour's text.
function paintLabels(ctx, L, features, px, alpha) {
  const { W, H, sx: SX, sy: SY, font: F, boost, rowY } = L;
  if (!features || !features.length) return;
  const nmin = L.nameMin || 0;
  const rowPx = Math.max(LABEL_ROW_PX * F, Math.max(nmin, px(19)) + px(11) + 4);
  const pad = Math.max(3, 3 * F);
  let n = 0;
  for (const f of features) {
    if (f.off || n === MAXL) continue;
    const sx = f.sx * SX, sy = f.sy * SY;
    let k = 1;
    if (boost) {
      const cen = Math.max(0, 1 - Math.hypot((sx - W / 2) / (W / 2),
        (sy - H / 2) * 0.75 / (H / 2)));
      k = 1 + cen * cen;
    }
    // landforms (summits, passes, ridges) share the summit style
    const kc = f.kind === 'peak' || f.kind === 'farpeak' || f.kind === 'pass' || f.kind === 'ridge' ? 0
      : f.kind === 'station' ? 1 : f.kind === 'lake' || f.kind === 'river' ? 2 : 3;
    const e = _entry(ctx, f, kc);
    const np = Math.round(Math.max(kc === 0 ? nmin : nmin - 1, px((kc === 0 ? 19 : 14) * k)));
    const dp = Math.round(px((kc === 0 ? 11 : 10) * k));
    const ly = rowY + (f.lift || 0) * rowPx;
    const tw = Math.max(e.w * np, e.dw * dp);
    _X0[n] = f.flip ? sx - 7 * F - tw - pad : sx - 5 * F;
    _X1[n] = f.flip ? sx + 5 * F : sx + 7 * F + tw + pad;
    _Y0[n] = ly - (kc === 0 ? 4 : 2) * F - 0.85 * np - pad;
    _Y1[n] = ly + 1.25 * dp + pad;
    _LY[n] = ly;
    _LIM[n] = Math.max(_Y1[n], sy - 10 * F);
    _DY[n] = 0;
    _SXa[n] = sx; _SYa[n] = sy; _TW[n] = tw;
    _NP[n] = np; _DP[n] = dp; _KC[n] = kc;
    _FE[n] = f; _EN[n] = e;
    n++;
  }
  if (L.avoid) {
    // row 0 first (insertion sort by baseline, stable)
    for (let i = 0; i < n; i++) {
      let j = i;
      while (j > 0 && _LY[_ORD[j - 1]] > _LY[i]) { _ORD[j] = _ORD[j - 1]; j--; }
      _ORD[j] = i;
    }
    placeLabels(n, _X0, _X1, _Y0, _Y1, _LY, _LIM, rowPx, L.avoid, L.nAvoid || 0, _ORD, _DY);
  }

  // pass 1: leader line from label down to feature position: halo, then core
  // (dashed when the object is hidden behind something), designator box,
  // diamond at the top of the leader — faded by alpha
  if (alpha < 1) { ctx.save(); ctx.globalAlpha = alpha; }
  for (let i = 0; i < n; i++) {
    const dy = _DY[i];
    if (dy !== dy) continue;
    const f = _FE[i], kc = _KC[i], sx = _SXa[i], sy = _SYa[i];
    const labelY = _LY[i] + dy;
    const core = kc <= 1 ? _cy(0.95) : kc === 2 ? _wa(0.95) : _am(0.95);
    ctx.setLineDash(f.occl ? [6 * F + 2, 4 * F + 2] : []);
    ctx.beginPath();
    ctx.moveTo(sx, labelY + 5 * F);
    ctx.lineTo(sx, sy - 6 * F);
    ctx.strokeStyle = HALO; ctx.lineWidth = Math.max(3.2, 3.6 * F); ctx.stroke();
    ctx.strokeStyle = core; ctx.lineWidth = Math.max(1.4, 1.6 * F); ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = HALO; ctx.lineWidth = Math.max(3, 3.4 * F);
    ctx.strokeRect(sx - 4 * F, sy - 4 * F, 8 * F, 8 * F);
    ctx.strokeStyle = kc === 0 ? 'rgba(255,110,110,0.95)'
      : kc === 1 ? _cy(0.95) : kc === 2 ? _wa(0.95) : 'rgba(255,200,80,0.95)';
    ctx.lineWidth = Math.max(1.4, 1.4 * F);
    ctx.strokeRect(sx - 4 * F, sy - 4 * F, 8 * F, 8 * F);
    ctx.beginPath();
    ctx.moveTo(sx, labelY + 7 * F);
    ctx.lineTo(sx - 3.5 * F, labelY + 3.5 * F);
    ctx.lineTo(sx, labelY);
    ctx.lineTo(sx + 3.5 * F, labelY + 3.5 * F);
    ctx.closePath();
    ctx.fillStyle = kc <= 1 ? _cy(0.7) : kc === 2 ? _wa(0.6) : _am(0.6);
    ctx.fill();
  }
  if (alpha < 1) ctx.restore();

  // pass 2: every pill, one path (overlaps never darken twice)
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const dy = _DY[i];
    if (dy !== dy) continue;
    const sx = _SXa[i], tw = _TW[i];
    const x = _FE[i].flip ? sx - 7 * F - tw - pad : sx + 7 * F - pad;
    const y = _Y0[i] + dy, h = _Y1[i] - _Y0[i];
    if (ctx.roundRect) ctx.roundRect(x, y, tw + 2 * pad, h, Math.min(6 * F + 2, h / 2));
    else ctx.rect(x, y, tw + 2 * pad, h);
  }
  ctx.fillStyle = PILL;
  ctx.fill();

  // pass 3: the text, opaque, under its dark outline
  ctx.lineJoin = 'round';
  for (let i = 0; i < n; i++) {
    const dy = _DY[i];
    if (dy !== dy) continue;
    const f = _FE[i], e = _EN[i], kc = _KC[i], sx = _SXa[i];
    const labelY = _LY[i] + dy;
    ctx.textAlign = f.flip ? 'right' : 'left';
    const tx = f.flip ? sx - 7 * F : sx + 7 * F;
    ctx.font = _font(kc <= 1 ? 0 : 1, _NP[i]);
    ctx.fillStyle = INK[kc];
    ctx.strokeStyle = 'rgba(0,0,0,0.8)';
    ctx.lineWidth = kc === 0 ? 3 : 2.5;
    const ny = labelY - (kc === 0 ? 4 : 2) * F;
    ctx.strokeText(e.s, tx, ny);
    ctx.fillText(e.s, tx, ny);
    ctx.font = _font(2, _DP[i]);
    ctx.fillStyle = SUB[kc];
    ctx.lineWidth = 2;
    ctx.strokeText(e.d, tx, labelY + _DP[i]);
    ctx.fillText(e.d, tx, labelY + _DP[i]);
  }
  ctx.lineJoin = 'miter';
  ctx.textAlign = 'center';
  for (let i = 0; i < n; i++) { _FE[i] = null; _EN[i] = null; }
}
// ------------------------------------------------------------ the view HUD
// Desktop + phone: one fullscreen canvas over the one view. Everything here
// morphs with w = ln Z / ln(fov0 / band fov) — 0 at 1×, 1 from the optic
// band on (viewzoom.js) — so there is no mode switch anywhere on screen.
const _lerp = (a, b, t) => a + (b - a) * t;
const _wrap180 = (d) => ((d + 180) % 360 + 360) % 360 - 180;

export function fmtRangeHud(m) {
  return m >= 10000 ? `${(m / 1000).toFixed(1)} km`
    : m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}

// The compass strip: always on, top centre (clear of the corner minimap).
// markers [{brg (deg), color}]: a diamond on the strip, or an edge arrow
// pointing the way when the bearing is off the strip.
export function paintCompass(ctx, x0, top, cw, s, bearing, markers) {
  const h = 26 * s;
  const vis = cw >= 420 ? 120 : 90;          // degrees across the strip
  const ppd = cw / vis;
  const cx = x0 + cw / 2;
  ctx.save();
  ctx.fillStyle = 'rgba(6,10,14,0.42)';
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x0, top, cw, h, h / 2); else ctx.rect(x0, top, cw, h);
  ctx.fill();
  ctx.save();
  ctx.beginPath();
  ctx.rect(x0 + 8 * s, top, cw - 16 * s, h);
  ctx.clip();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const start = bearing - vis / 2;
  for (let g = Math.ceil(start / 5) * 5; g <= bearing + vis / 2; g += 5) {
    const x = cx + (g - bearing) * ppd;
    const nd = ((g % 360) + 360) % 360;
    const cardFn = CARDINALS[nd];
    if (cardFn) {
      const big = nd % 90 === 0;
      ctx.font = `${big ? 'bold ' : ''}${Math.round((big ? 13 : 10.5) * s)}px system-ui, sans-serif`;
      ctx.fillStyle = nd === 0 ? 'rgba(255,125,110,0.98)' : big ? 'rgba(236,244,250,0.95)' : 'rgba(220,232,240,0.75)';
      ctx.fillText(cardFn(), x, top + h / 2 + 4.5 * s);
      continue;
    }
    const major = nd % 15 === 0;
    ctx.strokeStyle = major ? 'rgba(230,240,245,0.7)' : 'rgba(230,240,245,0.32)';
    ctx.lineWidth = 1;
    const th = (major ? 7 : 4) * s;
    ctx.beginPath();
    ctx.moveTo(x, top + h - th - 3 * s);
    ctx.lineTo(x, top + h - 3 * s);
    ctx.stroke();
    if (major && ppd * 15 >= 30 * s) {
      ctx.font = `${Math.round(9 * s)}px ui-monospace, monospace`;
      ctx.fillStyle = 'rgba(220,232,240,0.55)';
      ctx.fillText(String(nd), x, top + 11 * s);
    }
  }
  ctx.restore();
  // markers: waypoint beacon, the map pin, the followed player
  const half = vis / 2 - 4;
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = 'rgba(0,0,0,0.75)';
  for (const m of markers || []) {
    const dd = _wrap180(m.brg - bearing);
    ctx.fillStyle = m.color;
    ctx.beginPath();
    if (Math.abs(dd) <= half) {
      const x = cx + dd * ppd, y = top + h / 2, k = 5.5 * s;
      ctx.moveTo(x, y - k); ctx.lineTo(x + k, y); ctx.lineTo(x, y + k); ctx.lineTo(x - k, y);
    } else {
      const y = top + h / 2, k = 7 * s;
      if (dd < 0) { const x = x0 - 3 * s; ctx.moveTo(x - k, y); ctx.lineTo(x, y - k * 0.8); ctx.lineTo(x, y + k * 0.8); }
      else { const x = x0 + cw + 3 * s; ctx.moveTo(x + k, y); ctx.lineTo(x, y - k * 0.8); ctx.lineTo(x, y + k * 0.8); }
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  // caret at the heading + the boxed bearing under the strip
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.beginPath();
  ctx.moveTo(cx, top + 6 * s); ctx.lineTo(cx - 5 * s, top); ctx.lineTo(cx + 5 * s, top);
  ctx.closePath();
  ctx.fill();
  const bw = 44 * s, bh = 17 * s, by = top + h + 3 * s;
  ctx.fillStyle = 'rgba(6,10,14,0.6)';
  ctx.fillRect(cx - bw / 2, by, bw, bh);
  ctx.font = `bold ${Math.round(12 * s)}px ui-monospace, monospace`;
  ctx.fillStyle = 'rgba(240,248,252,0.95)';
  ctx.fillText(`${String(Math.round(bearing) % 360).padStart(3, '0')}°`, cx, by + bh * 0.76);
  ctx.restore();
}

// Paint the view HUD.
//   L = { W, H, sx, sy (gazetteer 1024×640 space → px), font (label scale),
//         boost (centre-boosted label fonts), rowY (label row baseline),
//         compassX, compassTop, compassW, magX (null = no MAG gauge — the
//         phone's zoom slider carries the readout) }
//   d = { bearing, markers, features, labelAlpha, w (0..1), band, zoom (Z),
//         zMax, zBand, range: {dist|null, dh|null, name, dim} | null,
//         frame, hit (0 | 1 hit | 2 kill flash) }
export function paintViewHud(ctx, L, d) {
  const { W, H, font: F } = L;
  const s = Math.max(0.85, Math.min(1.2, F));        // chrome scale
  const px = (n) => (F >= 1 ? n * F : Math.max(TXT_MIN, n * F));
  const w = Math.max(0, Math.min(1, d.w || 0));
  const CX = W / 2, CY = H / 2;

  paintLabels(ctx, L, d.features, px, d.labelAlpha == null ? 1 : d.labelAlpha);
  paintCompass(ctx, L.compassX, L.compassTop, L.compassW, s, d.bearing, d.markers);

  ctx.save();
  // --- reticle: the 1× centre dot grows rings + a broken cross with w ---
  if (w > 0.02) {
    const rot = d.band ? d.frame * 0.01 : 0;
    const R1 = _lerp(26, 54, w) * s, R2 = _lerp(19, 38, w) * s;
    ctx.strokeStyle = _cy(0.55 * w);
    ctx.lineWidth = 1.6;
    for (let i = 0; i < 2; i++) {
      ctx.beginPath();
      ctx.arc(CX, CY, R1, rot + i * Math.PI, rot + i * Math.PI + 1.1);
      ctx.stroke();
    }
    ctx.strokeStyle = _cy(0.3 * w);
    ctx.lineWidth = 1.2;
    for (let i = 0; i < 2; i++) {
      ctx.beginPath();
      ctx.arc(CX, CY, R2, -rot * 1.6 + i * Math.PI + 0.6, -rot * 1.6 + i * Math.PI + 1.6);
      ctx.stroke();
    }
    const ah = _lerp(30, 90, w) * s, av = _lerp(24, 70, w) * s, g = 14 * s;
    ctx.strokeStyle = `rgba(200,235,225,${0.4 * w})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(CX, CY - av); ctx.lineTo(CX, CY - g);
    ctx.moveTo(CX, CY + g); ctx.lineTo(CX, CY + av);
    ctx.moveTo(CX - ah, CY); ctx.lineTo(CX - g, CY);
    ctx.moveTo(CX + g, CY); ctx.lineTo(CX + ah, CY);
    ctx.stroke();
    if (d.band) {
      ctx.fillStyle = 'rgba(200,235,225,0.4)';
      for (const off of [-66, -44, -22, 22, 44, 66]) {
        ctx.beginPath();
        ctx.arc(CX + off * s, CY, 1.5 * s, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  // centre dot: white at 1×, the optic's red dot in the band; a hit flashes it
  const hr = d.hit === 2 ? 6 : d.hit === 1 ? 4.5 : _lerp(3, 2, w);
  ctx.beginPath();
  ctx.arc(CX, CY, hr * s + 1, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(CX, CY, hr * s, 0, Math.PI * 2);
  ctx.fillStyle = d.hit === 2 ? '#ffd23a' : d.hit === 1 ? '#ff5540'
    : `rgba(255,${Math.round(_lerp(255, 80, w))},${Math.round(_lerp(255, 80, w))},0.9)`;
  ctx.fill();

  // --- centre range: always on; Δh + the target's name from Z ≈ 2 ---
  const r = d.range;
  if (r) {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    const fs = _lerp(11, 16, w) * s;
    const ry = CY + _lerp(20, 86, w) * s + fs * 0.4;
    const al = r.dim ? 0.45 : 0.95;
    ctx.font = `bold ${Math.round(fs)}px ui-monospace, monospace`;
    ctx.lineWidth = 3;
    ctx.strokeStyle = `rgba(0,0,0,${0.55 * al})`;
    ctx.fillStyle = `rgba(${Math.round(_lerp(240, 255, w))},${Math.round(_lerp(246, 90, w))},${Math.round(_lerp(250, 80, w))},${al})`;
    const txt = r.dist == null ? '—' : fmtRangeHud(r.dist);
    ctx.strokeText(txt, CX, ry);
    ctx.fillText(txt, CX, ry);
    if (d.zoom >= 2) {
      const parts = [];
      if (r.name) parts.push(r.name);
      if (r.dh != null) parts.push(`Δh ${r.dh >= 0 ? '+' : '−'}${Math.abs(Math.round(r.dh))} m`);
      if (parts.length) {
        const f2 = _lerp(11, 13, w) * s;
        ctx.font = `600 ${Math.round(f2)}px system-ui, sans-serif`;
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.fillStyle = 'rgba(170,255,225,0.92)';
        const t2 = parts.join(' · ');
        ctx.strokeText(t2, CX, ry + f2 * 1.35);
        ctx.fillText(t2, CX, ry + f2 * 1.35);
      }
    }
  }

  // --- MAG gauge (desktop; Z > 1): log scale 1× → Zmax, band entry ticked ---
  if (L.magX != null && d.zoom > 1.01 && d.zMax > 1) {
    const gx = L.magX, gy0 = CY - 80 * s, gy1 = CY + 80 * s;
    const lm = Math.log(d.zMax);
    const f = Math.max(0, Math.min(1, Math.log(d.zoom) / lm));
    const fb = Math.max(0, Math.min(1, Math.log(d.zBand) / lm));
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(gx, gy0); ctx.lineTo(gx, gy1); ctx.stroke();
    ctx.strokeStyle = _cy(0.9);
    ctx.beginPath(); ctx.moveTo(gx, gy1); ctx.lineTo(gx, gy1 - f * (gy1 - gy0)); ctx.stroke();
    ctx.lineCap = 'butt';
    ctx.strokeStyle = 'rgba(255,200,90,0.85)';
    ctx.lineWidth = 1.5;
    const by = gy1 - fb * (gy1 - gy0);
    ctx.beginPath(); ctx.moveTo(gx - 6 * s, by); ctx.lineTo(gx + 6 * s, by); ctx.stroke();
    ctx.textAlign = 'center';
    ctx.font = `bold ${Math.round(13 * s)}px ui-monospace, monospace`;
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.fillStyle = 'rgba(240,248,252,0.95)';
    const zt = `${d.zoom.toFixed(d.zoom < 10 ? 1 : 0)}×`;
    ctx.strokeText(zt, gx, gy0 - 10 * s);
    ctx.fillText(zt, gx, gy0 - 10 * s);
  }
  ctx.restore();
  ctx.textAlign = 'start';
}

// The #view-hud canvas (index.html) for desktop + phone: sized to the
// viewport in CSS px (backing store at DPR ≤ 2), repainted by main.js after
// the frame renders. insets: the corner widgets the compass strip must clear
// (left/right = px reserved at the top corners; below = where the strip goes
// when the gap between them is too narrow — a portrait phone).
// The view HUD's labels (owner 2026-10-20 "on desktop move them higher. but
// avoid placing over the minimap"): the band starts LABEL_TOP px under the top
// edge and placeLabels drops a label out of these widgets (index.html /
// mobile.js; each rect padded AVOID_PAD): the minimap + 2D pill (#pmap), the
// "?" (#brief-help), the phone's 🔒, zoom slider and button column.
export const LABEL_TOP = 12;
export const AVOID_IDS = ['pmap', 'brief-help', 'mob-lock', 'mob-zoom', 'mob-right'];
const AVOID_PAD = 6;
function _avoidRect(a, n, x0, y0, x1, y1) {
  if (4 * n + 4 > a.length) return n;
  a[4 * n] = x0 - AVOID_PAD; a[4 * n + 1] = y0 - AVOID_PAD;
  a[4 * n + 2] = x1 + AVOID_PAD; a[4 * n + 3] = y1 + AVOID_PAD;
  return n + 1;
}

// the view HUD's layout on a W×H surface (CSS px on desktop / phone, the
// zoom window's canvas px in VR): the compass strip top centre clear of the
// side insets, the label band from LABEL_TOP, the MAG gauge at the right
// edge; the labels clear avoid's first nDom rects (the DOM widgets), the
// compass strip + its bearing box and, zoomed, the MAG gauge.
// o = { insets: {left, right, below}, boost, mag, compass (false: no strip to clear) }
function _viewLayout(W, H, o, avoid, nDom, d) {
  const font = Math.min(1.2, H / 640);
  const s = Math.max(0.85, Math.min(1.2, font));
  const side = Math.max(o.insets.left, o.insets.right);
  let cw = Math.min(520, W - 2 * side - 16), top = 12;
  if (cw < 240) { cw = Math.min(520, W - 24); top = o.insets.below || 12; }
  const cx0 = (W - cw) / 2;
  let na = o.compass === false ? nDom : _avoidRect(avoid, nDom, cx0, top, cx0 + cw, top + 46 * s);
  if (o.mag && d.zoom > 1.01 && d.zMax > 1)
    na = _avoidRect(avoid, na, W - 60 * s, H / 2 - 108 * s, W, H / 2 + 88 * s);
  // the label band starts at LABEL_TOP: row 0's pill top sits there
  const nameMin = o.boost ? PHONE_NAME_MIN : 0;
  const np = Math.round(Math.max(nameMin, font >= 1 ? 19 * font : Math.max(TXT_MIN, 19 * font)));
  return {
    W, H, sx: W / 1024, sy: H / 640,
    font, boost: o.boost, nameMin, avoid, nAvoid: na,
    rowY: LABEL_TOP + 0.85 * np + 4 * font + Math.max(3, 3 * font),
    compassX: cx0, compassTop: top, compassW: cw,
    magX: o.mag ? W - 34 * s : null,
  };
}

// VR: the head zoom window's overlay (its 1024×640 canvas, Weapons.drawLabels)
// = ONLY the desktop view HUD's LABELS over the magnified picture, by the same
// rules (gazetteer.inView's pre-band layer below the band, the optic's from
// it). No compass, reticle, range, MAG gauge or rim: the monocular look left
// VR (owner 2026-11-01 "plain"; vr-nomap) — the zoom N.N× is the VR HUD's.
// d = { features, labelAlpha }.
const _ZW = { insets: { left: 0, right: 0, below: 0 }, boost: false, mag: false, compass: false };
let _zwF = 1;
const _zwPx = (n) => (_zwF >= 1 ? n * _zwF : Math.max(TXT_MIN, n * _zwF));
const _zwAvoid = new Float32Array(4 * 4);
export function paintZoomWindow(ctx, d) {
  const W = ctx.canvas.width, H = ctx.canvas.height;
  ctx.clearRect(0, 0, W, H);
  const L = _viewLayout(W, H, _ZW, _zwAvoid, 0, d);
  _zwF = L.font;
  paintLabels(ctx, L, d.features, _zwPx, d.labelAlpha == null ? 1 : d.labelAlpha);
}

export class ScopeHud {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas ? canvas.getContext('2d') : null;
    this._w = 0;
    this._h = 0;
    this._dpr = 0;
    this._frame = 0;
    this.every = 1;            // paint every n-th call (phone: 2)
    this.boost = false;        // phone: centre-boosted label fonts
    this.mag = true;           // desktop: MAG gauge (phone: the slider)
    this.insets = { left: 0, right: 0, below: 0 };
    this._avoid = new Float32Array(4 * 12);   // HUD rects the labels clear
    this._nDom = 0;
  }

  // the DOM widgets' rects (AVOID_IDS; hidden ones are 0×0 and skipped) —
  // read on a resize and every 60th call (widgets show / hide without one)
  _readDom() {
    let n = 0;
    for (const id of AVOID_IDS) {
      const el = document.getElementById(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) n = _avoidRect(this._avoid, n, r.left, r.top, r.right, r.bottom);
    }
    this._nDom = n;
  }

  setInsets(o) { Object.assign(this.insets, o); }

  draw(d) {
    if (!this.ctx) return;
    this._frame++;
    if (this.every > 1 && this._frame % this.every) return;
    const W = innerWidth, H = Math.max(1, innerHeight);
    const dpr = Math.min(devicePixelRatio || 1, 2);
    if (W !== this._w || H !== this._h || dpr !== this._dpr) {
      this.canvas.width = Math.round(W * dpr);
      this.canvas.height = Math.round(H * dpr);
      this._w = W; this._h = H; this._dpr = dpr;
      this._readDom();
    } else if (this._frame % 60 === 0) this._readDom();
    const ctx = this.ctx;
    // lay out in CSS pixels; the backing store carries the device pixels
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    paintViewHud(ctx, _viewLayout(W, H, this, this._avoid, this._nDom, d), d);
  }

  clear() {
    if (!this.ctx) return;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
